import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Button } from '@/shared/ui/buttons/Button';
import { Select } from '@/shared/ui/selects/Select';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiRoleDelete } from '@/entities/roles';
import type { ApiRole } from '@/shared/types/api/roles';

type Props = {
  open: boolean;
  onClose: () => void;
  role: ApiRole | null;
  allRoles: ApiRole[];
};

export const RoleDeleteDialog = ({ open, onClose, role, allRoles }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();

  const options = useMemo(() => {
    if (!role) return [];
    return allRoles
      .filter((r) => r.id !== role.id && !r.is_super)
      .map((r) => ({ value: r.id, label: r.name }));
  }, [allRoles, role]);

  const [target, setTarget] = useState<string>('');

  useEffect(() => {
    if (open) setTarget(options[0]?.value ?? '');
  }, [open, options]);

  const remove = useMutate({
    api: ({ id, reassignTo }: { id: string; reassignTo: string }) =>
      apiRoleDelete(id, reassignTo),
    invalidateKeys: [queryKeys.roles.all],
    onSuccess: () => {
      toast.info(t('roles.toast.deleted'));
      onClose();
    },
    onError: (_e, m) => toast.error(m),
  });

  const noTargets = options.length === 0;
  // A reassignment target is always required before a role can be deleted.
  const canSubmit = Boolean(target) && !noTargets;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="md"
      title={t('roles.delete.title')}
      description={
        role
          ? t('roles.delete.description', { name: role.name })
          : undefined
      }
      closeOnBackdrop={!remove.isPending}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={remove.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              role &&
              canSubmit &&
              remove.mutate({ id: role.id, reassignTo: target })
            }
            loading={remove.isPending}
            disabled={!canSubmit}
          >
            {t('roles.delete.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {noTargets ? (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('roles.delete.noTargets')}</span>
          </div>
        ) : (
          <>
            {(role?.staff_count ?? 0) > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                {t('roles.delete.staffWarning', { count: role?.staff_count ?? 0 })}
              </div>
            )}
            <Select
              label={t('roles.delete.reassignLabel')}
              required
              options={options}
              value={target}
              onChange={(e) => setTarget(e.target.value)}
            />
            <p className="text-xs text-ink-muted">{t('roles.delete.reassignHint')}</p>
          </>
        )}
      </div>
    </Dialog>
  );
};
